import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';
import { Box, Button, Icon, IconButton, Paper, Tooltip } from '@mui/material';
import {
  CheckCircle,
  Verified,
  AccessTimeFilled,
  Error as ErrorIcon,
  BugReport,
  UploadFile as UploadFileIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';

import ErrorMessage from './ErrorMessage';
import ProgressBar from './ProgressBar';

import { GeoPackageType } from '../../models/types/geoPackageType';
import DialogUploadGeoPackage from './dialogs/DialogUploadGeoPackage';
import GeoPackageValidationResult from '../plannen/GeoPackageValidationResult';

import { planManagementService } from '../../services/plan-management.service';


interface ImportGeoPackageProps {
  type: GeoPackageType;
  organisatieId: string;
}

const ImportGeoPackage: React.FC<ImportGeoPackageProps> = ({ type, organisatieId }) => {
  // const LS_LAST_STATE_UPLOAD_GEOPACKAGE = `InfplLastStateGeoPackage_${type.toLowerCase()}`;
  // const LS_ORGANISATION_GEOPACKAGE = `InfplLastStateGeoPackage_Organisation_${type.toLowerCase()}`;

  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const [openUploadDialog, setOpenUploadDialog] = React.useState(false);
  const [statusDataArray, setStatusDataArray] = useState<any | null>(null); // TODO: Implement later
  const [statusData, setStatusData] = useState<any | null>(null);
  const [selectedTimestamp, setSelectedTimestamp] = useState<string | null>(null);
  const [details, setDetails] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Ref to prevent fetching data twice in Strict Mode
  const hasFetchedData = useRef(false);

  const fetchStatus = async () => {
    if (!organisatieId) {
      console.error('Organisatie id is null');
      return;
    }

    setLoading(true);
    try {
      const data = await planManagementService.getGeoPackageStatus(type);
      setLoading(false);
      
      if (data.length < 2) {
        let currentStatus = null;
        let statusSet = false;

        if (data.length === 1) {
          currentStatus = data[0];
        }

        // const lastStatus = localStorage.getItem(LS_LAST_STATE_UPLOAD_GEOPACKAGE);
        // const lastStatusOrganisation = localStorage.getItem(LS_ORGANISATION_GEOPACKAGE);

        // const parsedStatusOrganisation = lastStatusOrganisation && JSON.parse(lastStatusOrganisation);
        // const parsedStatus = lastStatus && JSON.parse(lastStatus);
        // const currentOrganisatie = userManagementService.getStoredOrganisatie();

        // // Compare organisatie from localStorage with the current one
        // if (currentOrganisatie?.id === parsedStatusOrganisation?.id) {
        //   // If the organisatie matches, set the status data
        //   if (parsedStatus && (!currentStatus || parsedStatus.timestamp !== currentStatus.timestamp)) {
        //     setStatusData(parsedStatus);
        //     statusSet = true;
        //   }
        // }

        if (!statusSet) {
          setStatusData(currentStatus);
        }

        // Trigger fetchDetails if relevant
        if (currentStatus?.status === 'validation-success' || 
            currentStatus?.status === 'validation-failed') {
          handleFetchDetails(currentStatus.timestamp);
        }

      }
      else {
        // Assuming the API response is a JSON object containing array of items
        // setStatusDataArray(data);
        setError('Er zijn meer dan 1 items gevonden met status informatie. Dit is (op dit moment) niet toegestaan.');
      }
    }
    catch (error: any) {
      setLoading(false);
      // If token refresh failed, redirect to the logout page
      if (error.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }
      setError(error.message || 'Er is een fout opgetreden');
    }
  };

  // Fetch the status data from API when the component mounts
  useEffect(() => {
    if (!hasFetchedData.current) {
      fetchStatus();
    } 
  }, []); // [] means only once

  const onClickOpenUploadDialog = (open: boolean = false) => {
    setOpenUploadDialog(open);
  };
  
  const handleSubmitUpload = async (file: File) => {
    setLoading(true);
    try {
      const result = await planManagementService.uploadGeoPackage(type, file);

      // localStorage.setItem(LS_LAST_STATE_UPLOAD_GEOPACKAGE, JSON.stringify(result));
      // const organisatie = userManagementService.getStoredOrganisatie();
      // localStorage.setItem(LS_ORGANISATION_GEOPACKAGE, JSON.stringify(organisatie)); // Set current organisation in localStorage as package organisation
      
      setTimeout(() => {
        setLoading(false);
        fetchStatus(); // Refresh data after delete
      }, 3000); // Add some delay
    } 
    catch (err: any) {
      
      if (err.status === 413) {
        setError('Bestand is te groot. De toegestane maximale bestandsgrootte is 150MB.');
      }
      else if (err.status === 422 || err.status === 500) {
        setError('Upload mislukt. Controleer uw bestand en probeer het opnieuw.');
      }
      else {
        setError(err.message || 'Upload mislukt');
      }
      setLoading(false);
    }
  };

  const handleDeleteGeoPackage = async (timestamp: string) => {
    setLoading(true);
    try {
      await planManagementService.deleteGeoPackage(type, timestamp);

      setDetails(null);
      setSelectedTimestamp(null);
      // localStorage.removeItem(LS_LAST_STATE_UPLOAD_GEOPACKAGE);

      setTimeout(() => {
        setLoading(false);
        fetchStatus(); // Refresh data after delete
      }, 3000); // Add some delay
    } 
    catch (error: any) {
      setLoading(false);
      setError(error.message || 'Fout bij verwijderen van geopackage');
    }
  };

  const handleRefreshStatus = async () => {
    fetchStatus();
  };

  const handleFetchDetails = async (timestamp: string) => {
    setLoading(true);
    try {
      const data = await planManagementService.getGeoPackageData(type, timestamp);

      console.log(data);

      setLoading(false);
      setSelectedTimestamp(timestamp);
      setDetails(data);
    } 
    catch (err: any) {
      setLoading(false);
      setError(err.message || 'Details ophalen mislukt');
    }
  };

  const handleProcessGeoPackage = async () => {
    if (!selectedTimestamp) return;
    setLoading(true);
    try {
      await planManagementService.processGeoPackage(type, selectedTimestamp);
      
      setTimeout(() => {
        setLoading(false);
        setDetails(null);
        setSelectedTimestamp(null);
        fetchStatus(); // Refresh data after delete
      }, 3000); // Add some delay
    } 
    catch (err: any) {
      setLoading(false);
      setError(err.message || 'Verwerken mislukt');
    }
  };
  
  const renderStatusIcon = (status: string, fontSize: number = 28) => {
    const iconProps = { sx: { fontSize } };
    switch (status) {
      case 'validation-pending':
        return <AccessTimeFilled color="warning" {...iconProps} />;
      case 'validation-success':
        return <CheckCircle color="success" {...iconProps} />;
      case 'validation-failed':
        return <ErrorIcon color="error" {...iconProps} />;
      case 'package-processing':
      case 'plan-processing':
      case 'asset-processing':
      case 'approved':
        return <Verified color="primary" {...iconProps} />;
      case 'processed':
        return <FlagIcon color="success" {...iconProps} />;
      case 'corrupt':
        return <BugReport color="error" {...iconProps} />;
      default:
        return <CloudUploadIcon color="secondary" {...iconProps} />;
    }
  };

  const renderStatusIconInfo = (status: string) => {
    switch (status) {
      case 'validation-pending':
        return "Wordt gecontroleerd";
      case 'validation-success':
        return "Controle afgerond"; // Validatie is klaar
      case 'validation-failed':
        return "Controle mislukt"; // Validatie is mislukt
      case 'default':
        return "Geopackage bestand uploaden"; // Default status
      case 'package-processing':
      case 'plan-processing':
      case 'asset-processing':
      case 'approved':
        return "Wordt verwerkt"; // Approved door gebruiker verwerken is gestart
      case 'processed':
        return "Succesvol verwerkt"; // Verwerken is klaar
      case 'corrupt':
        return "Verwerken mislukt";
      default:
        return "Geopackage bestand uploaden";
    }
  };

  const renderStatusInfo = (status: string): JSX.Element | null => {
    switch (status) {
      case 'validation-pending':
        return (
          <>
            <p>
              De inhoud van het bestand wordt gecontroleerd.<br/>
              Er wordt gecontroleerd of de structuur in het bestand correct is en of een plan voldoet aan de eisen en waarden bevat die door infraplannen.nl verwerkt kunnen worden.<br/>
              
              <a href="https://mijnaansluiting.atlassian.net/wiki/spaces/Wiki/pages/628326403/Technische+specificatie+infraplannen.nl" target="_blank" rel="noopener noreferrer">
                Meer info
              </a>
            </p>
          </>
        );
  
      case 'validation-success':
        return (
          <>
            <p>
              Het bestand voldoet aan de eisen en waarden die door infraplannen.nl verwerkt kunnen worden.<br/>
              Controleer het resultaat. Klopt het? Dan kunt u besluiten dit bestand te laten verwerken.
            </p>
          </>
        );
  
      case 'validation-failed':
        return (
          <>
            <p>
              Het bestand bevat foutieve waarden die niet verwerkt kunnen worden door infraplannen.nl. 
              Pas uw bestand aan en probeer het opnieuw.<br/>
              Er wordt gecontroleerd of de structuur in het bestand correct is en of een plan voldoet aan de eisen en waarden bevat die door infraplannen.nl verwerkt kunnen worden.<br/> 
              
              <a href="https://mijnaansluiting.atlassian.net/wiki/spaces/Wiki/pages/628326403/Technische+specificatie+infraplannen.nl" target="_blank" rel="noopener noreferrer">
                Meer info
              </a> 
            </p>
          </>
        );
  
      case 'package-processing':
      case 'plan-processing':
      case 'asset-processing':
      case 'approved':
        return (
          <>
            <p>De verwerking van het bestand is gestart. Een moment geduld.</p>
          </>
        );
  
      case 'processed':
        return (
          <>
            <p>Het bestand is succesvol verwerkt.</p>
          </>
        );
  
      case 'corrupt':
        return (
          <>
            <p>Het verwerken van het bestand is mislukt. Neem contact op met de Helpdesk van Infraplannen.nl.</p>
          </>
        );
  
      default:
        return (
          <>
            <p>Er is geen status data gevonden van een eerder geupload geopackage. Deze bestaat nog niet.</p>
          </>
        );
    }
  };

  const hasPlanChanges = (result: any) => {
    return (
      result.plansToAddCount > 0 ||
      result.plansToRemoveCount > 0 ||
      result.plansToUpdateCount > 0
    );
  };
  
  const hasAssetChanges = (result: any) => {
    return (
      result.assetsToAddCount > 0 ||
      result.assetsToRemoveCount > 0 ||
      result.assetsToUpdateCount > 0
    );
  };
  
  const hasAnyChanges = (result: any) => {
    return hasPlanChanges(result) || hasAssetChanges(result);
  };
  

  return (
    <>
      {isAuthenticated && (
        <>
          <Paper sx={{ width: '100%', maxWidth: '1600px', padding: '20px', margin: '0px auto 50px' }}>

            {/* Incase of just one item */}
            {statusData ? (
              <>
                <div className="center">
                  <div className="status">
                    <div className="status-icon">
                      {renderStatusIcon(statusData.status, 48)} {/* groter icoon */}
                    </div>
                    <div className={`status-info ${statusData.status}`}>
                      { renderStatusIconInfo(statusData.status) }
                    </div>  
                  </div>
                  <div className="description">
                    { renderStatusInfo(statusData.status) }
                  </div>
                  <div className="timestamp">
                    <span className="timestamp-name">
                      {statusData.timestamp}
                    </span>

                    {statusData &&
                      statusData.status !== 'processed' &&
                      statusData.status !== 'package-processing' &&
                      statusData.status !== 'plan-processing' &&
                      statusData.status !== 'asset-processing' &&
                      statusData.status !== 'approved' && (
                      <>
                        <Tooltip title="Verwijder deze geopackage" placement="top-start" arrow>
                          <IconButton
                            onClick={() => handleDeleteGeoPackage(statusData.timestamp)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}

                    {statusData && statusData.status !== 'processed' && (
                      <>
                        <Tooltip title="Laatste status vernieuwen" placement="top-start" arrow>
                          <IconButton
                            onClick={() => handleRefreshStatus()}
                          >
                            <RefreshIcon /> 
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    
                  </div>

                  {/* <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(statusData, null, 2)}</pre> */}
                </div>
              </>
            ) : (
              <>
                <div className="center">
                  <div className="status">
                    <div className="status-icon">
                      {renderStatusIcon('default', 48)} {/* groter icoon */}
                    </div>
                    <div className={`status-info default`}>
                      { renderStatusIconInfo('default') }
                    </div>
                  </div>
                  <div className="description">
                    { renderStatusInfo('default') }
                  </div>
                </div>
              </>
            )}

            {(!statusData || statusData && statusData.status === 'processed') && (
              <>
                <div className="center">
                  <Button
                    variant="text"
                    color="secondary"
                    startIcon={<UploadFileIcon />}
                    onClick={() => onClickOpenUploadDialog(true)}
                  >
                    Upload geopackage
                  </Button>
                </div>
              </>
            )}

            {/* Incase of an array */}
            {Array.isArray(statusDataArray) && (
                <>
                  {(statusDataArray.length === 0 || (statusDataArray.length === 1 && statusDataArray[0].status === 'processed')) ? (
                    <Button
                      variant="text"
                      color="secondary"
                      startIcon={<UploadFileIcon />}
                      onClick={() => onClickOpenUploadDialog(true)}
                    >
                      Upload geopackage
                    </Button>
                  ) : (
                    statusDataArray.map((item: any, index: number) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          { renderStatusIcon(item.status) }
                          <div>
                            <strong>Status:</strong> { renderStatusIconInfo(item.status) }<br />
                            <strong>Timestamp:</strong> {item.timestamp} 
                            {/* {new Date(item.timestamp).toLocaleDateString('nl-NL')} : */}
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {(item.status === 'validation-success' || item.status === 'validation-failed') && (
                            <Button
                              variant="text"
                              color="secondary"
                              onClick={() =>
                                selectedTimestamp === item.timestamp
                                  ? setSelectedTimestamp(null) // sluit details
                                  : handleFetchDetails(item.timestamp) // toon details
                              }
                            >
                              {selectedTimestamp === item.timestamp ? 'Sluit resultaten' : 'Bekijk resultaten'}
                            </Button>
                          )}
                          <IconButton
                            onClick={() => handleDeleteGeoPackage(item.timestamp)}
                            title="Verwijder deze geopackage"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </div>
                      </div>
                    ))
                    
                  )}
                </>
              )}

              {details && selectedTimestamp && (
                <>
                  {/* <Paper sx={{ mt: 2, p: 2, backgroundColor: '#f9f9f9' }}> */}
                    {/* <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(details, null, 2)}</pre> */}

                    {details && (
                      <GeoPackageValidationResult type={type} result={details.result} success={details.success} packageErrors={details.packageErrors} />
                    )}

                    {details?.success && details?.result && hasAnyChanges(details.result) && (
                      <Box mt={2} display="flex" gap={1}>
                        <Button 
                          variant="contained" 
                          color="secondary"
                          startIcon={<Icon>rocket_launch</Icon>}
                          onClick={ handleProcessGeoPackage }>
                            Start verwerken
                        </Button>
                        {/* <Button 
                          variant="text"
                          color="secondary"
                          onClick={() => { setDetails(null); setSelectedTimestamp(null); }}>
                          Sluiten
                        </Button> */}
                      </Box>
                    )}

                    {details?.success && details?.result && !hasAnyChanges(details.result) && (
                      <>
                        <div style={{ marginTop: '30px' }}>
                          Er zijn geen wijzigingen gevonden in de geopackage. Dat betekent dat er niets te verwerken is. 
                          Verwijder deze geopackage en upload een andere geopackage.
                        </div>
                      </>
                    )}

                  {/* </Paper> */}
                </>
              )}

          </Paper>

          <DialogUploadGeoPackage
            open={openUploadDialog}
            title={ type === GeoPackageType.ASSET ? 'Upload uw kaartlagen' : 'Upload uw plannen' }
            onClose={() => setOpenUploadDialog(false)}
            onSubmit={handleSubmitUpload}
          />
        </>
      )}

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}
    </>
  );
};

export default ImportGeoPackage;